import { Control, UseFormRegister, useFormState } from 'react-hook-form';
import styles from './PricingTablePriceInput.module.scss';
import classes from 'classnames/bind';
import { memo, RefObject } from 'react';
import { IPriceRule } from '../../types/price.types';
import { usePlaceholder } from '../../hooks/usePlaceholder/usePlaceholder';
import { IPricingCategory } from '../../api/getPricingCategories/types';
import { CalculatorIcon } from '@weareopenr/component-library';
import { PriceRuleToolTip } from './PriceRuleToolTip';
import * as Popover from '@radix-ui/react-popover';
import PriceInputBase from '../PriceInputBase/PriceInputBase';

const cx = classes.bind(styles);

export const PriceInput = memo(function PriceInput({
  inputName,
  register,
  control,
  tableContainerRef,
  priceRules,
  categoryId,
  pricingCategories,
}: {
  inputName: string;
  register: UseFormRegister<any>;
  control: Control;
  tableContainerRef: RefObject<HTMLElement | null>;
  priceRules?: IPriceRule[];
  categoryId?: string | null;
  pricingCategories?: IPricingCategory[];
}) {
  const fields = register(inputName, {
    validate: (v) => !v || !isNaN(Number(v)),
  });

  const { dirtyFields, errors } = useFormState({ control, name: [inputName] });
  const { calculatedPlaceholder, inheritedPlaceholder, calculationSteps, inputValue } = usePlaceholder(
    inputName,
    control,
    priceRules,
    categoryId,
    pricingCategories,
  );

  return (
    <div className={cx('price-input-wrapper')}>
      {calculatedPlaceholder && calculationSteps?.length > 0 && (
        <Popover.Root>
          <Popover.Portal>
            <PriceRuleToolTip calculationSteps={calculationSteps} tableContainerRef={tableContainerRef} />
          </Popover.Portal>

          <Popover.Trigger aria-label="Calculated Price" className={cx('calculator-button')}>
            <CalculatorIcon className={cx('calculator-icon')} />
          </Popover.Trigger>
        </Popover.Root>
      )}

      <PriceInputBase
        id={inputName}
        error={!!errors[inputName] || (!inheritedPlaceholder && !calculatedPlaceholder && inputValue === null)}
        dirty={!!dirtyFields[inputName]}
        placeholder={calculatedPlaceholder || inheritedPlaceholder}
        data-testid={inputName}
        {...fields}
      />
    </div>
  );
});
